import { gql, useApolloClient, useMutation, useQuery } from "@apollo/client"
import { useCallback, useState } from "react"
import apolloClient from "../../apolloClient"
import { COACH_TOOLS_API } from "../../apolloClient"
import { getMember, getMemberForEdit, USER_QUERY } from "./member"
import {
  CLOUDINARY_RATE_LIMITED,
  uploadMemberCardAsset,
  uploadProfilePhotoFallback,
  uploadProfilePhotoOnly,
} from "../../utils/cloudinary/profilePhotoUpload"

// The profile-photo flow (validateProfilePhoto + updatePerson + currentPerson)
// only exists on the coach-tools GraphQL endpoint - the legacy v3 endpoint
// at REACT_APP_GRAPHQL_ENDPOINT does not expose any of it. Every operation
// in this file is routed through the splitLink via this context flag.
const coachToolsContext = { context: { api: COACH_TOOLS_API } }

export const CURRENT_PERSON_PGA_ID = gql`
  query CurrentPersonPgaId {
    currentPerson {
      id
      pgaId
      profilePhoto
    }
  }
`

export const VALIDATE_PROFILE_PHOTO = gql`
  mutation ValidateProfilePhoto($input: ValidateProfilePhotoInput!) {
    validateProfilePhoto(input: $input) {
      approved
      reasons
    }
  }
`

export const UPDATE_PERSON_PROFILE_PHOTO = gql`
  mutation UpdateMemberProfilePhoto($input: CRMUpdatePersonInput!) {
    updatePerson(input: $input) {
      person {
        id
        profilePhoto
      }
      message
      success
    }
  }
`

// When coach-tools `profilePhoto` is unavailable after Save, reload once
// so any remaining v3-backed surfaces pick up the CCMS sync.
export const PROFILE_PHOTO_SAVE_RELOAD_MS = 5000

// String names are fine for v3 queries; coach-tools queries must pass
// `{ query, context }` or refetch hits the wrong splitLink endpoint.
export const buildProfilePhotoRefetchQueries = memberId => [
  "ProfileBriefing",
  "USER_QUERY",
  "MembershipJourneyQuery",
  ...(memberId
    ? [{ query: getMemberForEdit, variables: { id: memberId } }]
    : ["GetMemberForEdit"]),
  { query: CURRENT_PERSON_PGA_ID, ...coachToolsContext },
]

export const PROFILE_PHOTO_REFETCH_QUERIES = buildProfilePhotoRefetchQueries()

const sendRaygun = error => {
  if (typeof window !== "undefined" && typeof window.rg4js === "function") {
    window.rg4js("send", { error })
  }
}

const HANDLED_ERROR_NAME = "ProfilePhotoHandledError"

export const createHandledError = message => {
  const err = new Error(message)
  err.name = HANDLED_ERROR_NAME
  return err
}

export const isHandledError = err =>
  err instanceof Error && err.name === HANDLED_ERROR_NAME

export const scheduleProfilePhotoReload = (
  delayMs = PROFILE_PHOTO_SAVE_RELOAD_MS,
) => {
  if (typeof window !== "undefined") {
    window.setTimeout(() => window.location.reload(), delayMs)
  }
}

export const writeAvatarToCache = (client, profileUrl) => {
  try {
    const data = client.cache.extract()
    Object.keys(data).forEach(cacheId => {
      if (cacheId === "ROOT_QUERY" || cacheId === "ROOT_MUTATION") return
      const entity = data[cacheId]
      if (!entity || typeof entity !== "object") return
      const fields = {}
      if ("photo" in entity) fields.photo = () => profileUrl
      if ("profilePhoto" in entity) fields.profilePhoto = () => profileUrl
      if (Object.keys(fields).length > 0) {
        client.cache.modify({ id: cacheId, fields })
      }
    })
  } catch (e) {
    // cache write is a best-effort optimization; refetch will catch up
  }
}

const writeQueryMemberPhoto = (client, query, profileUrl) => {
  try {
    client.cache.updateQuery({ query }, existing => {
      if (!existing || !existing.me) return existing
      return {
        ...existing,
        me: { ...existing.me, photo: profileUrl },
      }
    })
  } catch (e) {
    // query not in cache yet
  }
}

/** Eagerly update coach-tools + v3 member photo queries so useQuery hooks re-render. */
export const writeCoachToolsProfilePhotoToCache = (client, profileUrl) => {
  writeAvatarToCache(client, profileUrl)
  try {
    client.cache.updateQuery(
      { query: CURRENT_PERSON_PGA_ID, ...coachToolsContext },
      existing => {
        if (!existing || !existing.currentPerson) return existing
        return {
          currentPerson: {
            ...existing.currentPerson,
            profilePhoto: profileUrl,
          },
        }
      },
    )
  } catch (e) {
    // query not in cache yet; entity-level writeAvatarToCache still helps
  }
  writeQueryMemberPhoto(client, getMember, profileUrl)
  writeQueryMemberPhoto(client, USER_QUERY, profileUrl)
}

export const refreshCoachToolsProfilePhoto = client =>
  client
    .query({
      query: CURRENT_PERSON_PGA_ID,
      ...coachToolsContext,
      fetchPolicy: "network-only",
    })
    .catch(() => undefined)

export const useCoachToolsProfilePhoto = () => {
  const { data } = useQuery(CURRENT_PERSON_PGA_ID, {
    ...coachToolsContext,
    fetchPolicy: "cache-first",
  })
  return (data && data.currentPerson && data.currentPerson.profilePhoto) || null
}

const runValidateProfilePhoto = async (
  validateProfilePhotoMutation,
  imageUrl,
) => {
  try {
    const { data } = await validateProfilePhotoMutation({
      variables: { input: { imageUrl } },
    })
    const payload = data && data.validateProfilePhoto
    const approved = !!(payload && payload.approved === true)
    const reasons = ((payload && payload.reasons) || []).filter(
      r => typeof r === "string" && r.length > 0,
    )
    return { approved, reasons }
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err)
    if (
      /Rate Limit Exceeded/i.test(message) ||
      /Cloudinary AI Content Analysis/i.test(message) ||
      /monthly billing usage/i.test(message)
    ) {
      sendRaygun(
        new Error(`ProfilePhoto[validate-rate-limited-fail-open]: ${message}`),
      )
      return { approved: true, reasons: [] }
    }
    throw err
  }
}

const uploadProfilePhotoWithFallback = async file => {
  try {
    return await uploadProfilePhotoOnly(file)
  } catch (err) {
    if (err && err.code === CLOUDINARY_RATE_LIMITED) {
      sendRaygun(
        new Error(`ProfilePhoto[upload-rate-limited-fallback]: ${err.message}`),
      )
      return uploadProfilePhotoFallback(file)
    }
    throw err
  }
}

/**
 * Steps 1-2: Cloudinary upload + validateProfilePhoto moderation gate.
 * Does NOT persist to coach-tools — caller stores the returned URL in
 * form state and commits on Save via `commitMemberProfilePhoto`.
 */
export const prepareMemberProfilePhoto = async (
  file,
  validateProfilePhotoMutation,
) => {
  const profileResult = await uploadProfilePhotoWithFallback(file)
  const { profileUrl } = profileResult
  if (!profileUrl) {
    throw new Error("Cloudinary upload returned an empty profileUrl")
  }

  const validation = await runValidateProfilePhoto(
    validateProfilePhotoMutation,
    profileUrl,
  )

  if (!validation.approved) {
    const reasons = validation.reasons.length
      ? validation.reasons
      : ["This photo doesn't meet our guidelines. Please try again."]
    throw createHandledError(
      `ProfilePhoto[validate-rejected]: ${reasons.join("\n")}`,
    )
  }

  return { profileUrl, reasons: validation.reasons }
}

/**
 * Steps 3-4: member-card asset (best-effort) + coach-tools updatePerson.
 * Invoked from the profile panel Save handler, not on file pick.
 */
export const commitMemberProfilePhoto = async ({
  profileUrl,
  file,
  pgaId,
  memberId,
  client = apolloClient,
}) => {
  let memberCardWritten = false
  if (file && pgaId) {
    try {
      await uploadMemberCardAsset(file, { pgaId })
      memberCardWritten = true
    } catch (err) {
      sendRaygun(
        new Error(
          `ProfilePhoto[member-card-upload]: ${
            err instanceof Error ? err.message : String(err)
          }`,
        ),
      )
    }
  }

  const { data } = await client.mutate({
    mutation: UPDATE_PERSON_PROFILE_PHOTO,
    variables: {
      input: { addresses: [], profilePhotoUrl: profileUrl },
    },
    ...coachToolsContext,
    refetchQueries: buildProfilePhotoRefetchQueries(memberId),
    awaitRefetchQueries: false,
  })

  if (!data || !data.updatePerson || !data.updatePerson.success) {
    throw new Error(
      (data && data.updatePerson && data.updatePerson.message) ||
        "Could not save your photo. Please try again.",
    )
  }

  const coachToolsProfilePhoto =
    data &&
    data.updatePerson &&
    data.updatePerson.person &&
    data.updatePerson.person.profilePhoto
  const savedProfileUrl = coachToolsProfilePhoto || profileUrl
  writeCoachToolsProfilePhotoToCache(client, savedProfileUrl)
  await refreshCoachToolsProfilePhoto(client)
  return {
    profileUrl: savedProfileUrl,
    memberCardWritten,
    coachToolsProfilePhoto: savedProfileUrl,
  }
}

const useMemberProfilePhotoUpdate = ({ pgaId: pgaIdProp, onPrepared } = {}) => {
  const [preparing, setPreparing] = useState(false)
  const [error, setError] = useState(null)
  const [rejectionReasons, setRejectionReasons] = useState([])

  const { data: personData } = useQuery(CURRENT_PERSON_PGA_ID, {
    ...coachToolsContext,
    skip: !!pgaIdProp,
    fetchPolicy: "cache-first",
  })
  const pgaId =
    pgaIdProp ||
    (personData && personData.currentPerson && personData.currentPerson.pgaId)

  const [validateProfilePhotoMutation] = useMutation(
    VALIDATE_PROFILE_PHOTO,
    coachToolsContext,
  )

  const preparePhoto = useCallback(
    async file => {
      if (!pgaId) {
        const message =
          "Your account is missing a PGA ID. Please try again later."
        setError(message)
        throw createHandledError(message)
      }
      setPreparing(true)
      setError(null)
      setRejectionReasons([])

      try {
        const result = await prepareMemberProfilePhoto(
          file,
          validateProfilePhotoMutation,
        )
        const payload = { profileUrl: result.profileUrl, file, pgaId }
        if (onPrepared) onPrepared(payload)
        return payload
      } catch (err) {
        const message =
          err instanceof Error && err.message
            ? err.message
            : "Could not update your photo. Please try again."
        const isGuidelineRejection = /^ProfilePhoto\[validate-rejected\]:/.test(
          message,
        )
        if (isGuidelineRejection) {
          const userMessage = message.replace(
            /^ProfilePhoto\[validate-rejected\]:\s*/,
            "",
          )
          const reasons = userMessage.split("\n").filter(Boolean)
          setRejectionReasons(
            reasons.length
              ? reasons
              : ["This photo doesn't meet our guidelines. Please try again."],
          )
          throw createHandledError(userMessage)
        }
        const userMessage = message.replace(/^ProfilePhoto\[[^\]]+\]:\s*/, "")
        setError(userMessage)
        if (!isHandledError(err) && !/^ProfilePhoto\[[^\]]+\]:/.test(message)) {
          sendRaygun(
            new Error(
              `ProfilePhoto[run]: ${err instanceof Error ? err.message : String(err)}`,
            ),
          )
        }
        throw isHandledError(err) ? err : createHandledError(userMessage)
      } finally {
        setPreparing(false)
      }
    },
    [pgaId, validateProfilePhotoMutation, onPrepared],
  )

  return {
    preparePhoto,
    preparing,
    error,
    rejectionReasons,
    reset: () => {
      setError(null)
      setRejectionReasons([])
    },
  }
}

export default useMemberProfilePhotoUpdate
